import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React, {useEffect, useState} from 'react';

import {getArchiveItemName} from 'archive/ArchivePurposesUtil';
import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import ArchiveOrUnarchiveButton from 'archive/components/buttons/ArchiveOrUnarchiveButton';
import ExportAuditLogButton from 'audit/ExportAuditLogButton';
import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import FolderSelector from 'file-browser/FolderSelector';
import {BioentitySummary, SchemaTag} from 'folder-items/FileConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  InstrumentServiceDetails,
  ConnectionStatusResponse,
  ConnectionStatus,
  ConnectionFieldType,
} from 'instruments/connections/ConnectionConstants';
import {
  bulkArchive,
  bulkUnarchive,
  getAdapter,
  getInstrumentServiceDetails,
  updateConnection,
} from 'instruments/connections/ConnectionDAO';
import ConnectionStatusDisplay from 'instruments/connections/ConnectionStatusDisplay';
import getCustomTagEditors from 'instruments/connections/ConnectionTagCustomEditors';
import {EquipmentEditableField} from 'instruments/equipment/EquipmentPicker';
import {InstrumentGateway} from 'instruments/instrument-gateways/InstrumentGatewayConstants';
import {getGateway} from 'instruments/instrument-gateways/InstrumentGatewayDAO';
import AliasField from 'metadata/AliasField';
import {LoadingFileScreen} from 'nav/full-screen/LoadingFullScreen';
import {ValidationInfo} from 'schemas/tags/EditableSchemaTag';
import SchemaTagInputs from 'schemas/tags/SchemaTagInputs';
import Avatar from 'ui-library/avatar/Avatar';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {Title} from 'ui-library/Headers';
import runModal from 'ui-library/runModal';

import {ConnectionQuery, ConnectionQueryVariables} from './__generated/ConnectionQuery';
import ConnectionCollaboratorsModal from './ConnectionCollaboratorsModal';
import {convertToSchemaTag, getConnectionTagsToSet} from './ConnectionEditorUtils';
import DisableConnectionButton from './DisableConnectionButton';

export const CONNECTION_QUERY: DocumentNode = gql`
  query ConnectionQuery($connectionId: StrictString!) @skipAudit @engComponent(name: INSTRUMENTS) {
    connection(id: $connectionId) {
      ... on ConnectionNode {
        id
        name
        humanId
        permissions {
          readable
          writable
          archivable
          unarchivable
        }
        schema {
          id
          name
          archiveRecord {
            purpose
          }
        }
        creator {
          id
          name
          handle
          avatarUrl
        }
        dataCatalogFolder {
          id
        }
        createdAt
        fields
        ...HasArchiveRecordGraphQL
        associatedEquipment {
          ...BioentitySummaryGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('HasArchiveRecord').fragment}
  ${getFragmentWrapper('BioentitySummary').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

type Connection = ExtractByTypename<ConnectionQuery['connection'], 'ConnectionNode'>;

export interface ConnectionDetailsProps {
  connection: Connection;
  refetch: () => void;
  gateway: InstrumentGateway | null;
  status: ConnectionStatusResponse | null;
  tags: Array<SchemaTag>;
  fieldInfos: {[id: string]: string | null};
  fieldValidationInfos: {[id: string]: ValidationInfo};
  fieldTypes: {[id: string]: string | null};
}

export function ConnectionDetails(props: ConnectionDetailsProps): JSX.Element {
  const connection = props.connection;
  const connectionId = connection.id;

  const _handleUpdateIsEnabled = async (isEnabled: boolean): Promise<void> => {
    await updateConnection(connectionId, {isEnabled});
    props.refetch();
  };

  const _handleUpdateTags = async (tags: Array<SchemaTag>): Promise<void> => {
    const tagsToSet = getConnectionTagsToSet(tags);
    await updateConnection(connectionId, {tags: tagsToSet});
    props.refetch();
  };

  const _handleUpdateDataCatalogFolderId = async (e: {target: {value: string}}): Promise<void> => {
    await updateConnection(connectionId, {dataCatalogFolderId: e.target.value});
    props.refetch();
  };

  const _handleUpdateEquipment = async (equipment: Array<BioentitySummary>): Promise<void> => {
    await updateConnection(connectionId, {equipmentIds: equipment.map((item) => item.id)});
    props.refetch();
  };

  const permissions = connection.permissions;
  const isWritable = permissions.writable;
  const statusValue = props.status?.value;

  return (
    <div className="react-tab-container">
      <div>
        <div>
          <Row bufferRowReset={true}>
            <Col xs={6}>
              <AliasField
                initialAlias={connection.name}
                isFullWidth={true}
                isHeader={true}
                isWritable={isWritable}
                complete={async (name: string) => {
                  await updateConnection(connectionId, {name});
                  props.refetch();
                }}
                placeholder="Name"
              />
              <ArchiveRecordLabel item={{...connection, archiveRecord: connection.archiveRecord}} />
            </Col>
            <Col xs={6}>
              <div className="u-justify-content-end">
                <ArchiveOrUnarchiveButton
                  className="metadata-header-button-spacing-left"
                  isArchivable={Boolean(permissions.archivable)}
                  isUnarchivable={Boolean(permissions.unarchivable)}
                  item={connection}
                  getAnalyticsMetadata={() => ({
                    actionOrigin: ActionOrigin.CONNECTION_PAGE,
                  })}
                  onArchive={async (ids: Array<string>, purpose: string) => {
                    await bulkArchive(ids, purpose);
                    props.refetch();
                  }}
                  onUnarchive={async (ids: Array<string>) => {
                    await bulkUnarchive(ids);
                    props.refetch();
                  }}
                />
                <DisableConnectionButton status={props.status} updateIsEnabled={_handleUpdateIsEnabled} />
                <ExportAuditLogButton className="metadata-header-button-spacing-left" ids={[connectionId]} />
                <Button
                  className="metadata-header-button-spacing-left"
                  icon="settings"
                  buttonStyle={ButtonStyle.TERTIARY_BLUE}
                  size={ButtonSize.SMALL}
                  onClick={() => runModal(ConnectionCollaboratorsModal, {connectionId})}
                />
              </div>
            </Col>
          </Row>
        </div>
        <div>
          <Row bufferRowReset={true}>
            {statusValue ? (
              <Col xs={12}>
                <ConnectionStatusDisplay status={statusValue} />
              </Col>
            ) : null}
          </Row>
        </div>
      </div>
      <div className="u-spacing-top">
        <FormGroup>
          <Row bufferRowReset={true}>
            <Col xs={6}>
              <Title>Creator</Title>
              <div>
                <Avatar
                  className="u-mini-spacing-right"
                  size={AvatarSize.XXSMALL}
                  entity={connection.creator}
                />
                {connection.creator.name}
              </div>
            </Col>
            <Col xs={6}>
              <Title>Type</Title>
              <div>{getArchiveItemName(connection.schema)}</div>
            </Col>
          </Row>
        </FormGroup>
        <div className="u-spacing-top">
          <FormGroup>
            <Row bufferRowReset={true}>
              <Col xs={6}>
                <Title>Benchling Gateway</Title>
                <div>{props.gateway?.name}</div>
              </Col>
              {BenchlingConfig.FILES_ENABLE && (
                <Col xs={6}>
                  <Title>Benchling Location</Title>
                  <FolderSelector
                    value={connection.dataCatalogFolder?.id}
                    onChange={_handleUpdateDataCatalogFolderId}
                    showInventory={true}
                    readOnly={!isWritable}
                    unsetOnFetchFail={false}
                  />
                </Col>
              )}
            </Row>
          </FormGroup>
        </div>
        <div className="u-spacing-top">
          <SchemaTagInputs
            className="bufferRowReset u-small-spacing-top"
            itemLinkAllowed={false}
            isWritable={isWritable}
            onUpdateTags={_handleUpdateTags}
            tags={props.tags}
            tagPopovers={props.fieldInfos}
            tagValidationInfos={props.fieldValidationInfos}
            customEditors={getCustomTagEditors(props.fieldTypes)}
          />
        </div>
        {BenchlingConfig.ENABLE_EQUIPMENT && (
          <div className="u-spacing-top">
            <FormGroup>
              <Row bufferRowReset={true}>
                <Col xs={6}>
                  <Title>Associated Equipment</Title>
                  <EquipmentEditableField
                    shouldAllowMultiple={true}
                    initialSelection={props.connection.associatedEquipment.map((item) =>
                      graphQLToREST('BioentitySummary', item)
                    )}
                    onComplete={_handleUpdateEquipment}
                    isWriteable={true}
                  />
                </Col>
              </Row>
            </FormGroup>
          </div>
        )}
      </div>
    </div>
  );
}

export function poll(callback: () => void, ms: number): void {
  setTimeout(() => {
    callback();
    poll(callback, ms);
  }, ms);
}

const CONNECTION_POLL_INTERVAL_SEC = 10;

export default function QueryingConnectionDetails(props: {connectionId: string}): JSX.Element {
  const connectionId = props.connectionId;
  const result = useBenchlingQuery<ConnectionQuery, ConnectionQueryVariables>(CONNECTION_QUERY, {
    variables: {connectionId},
  });

  const [instrumentServiceDetails, setInstrumentServiceDetails] = useState(
    null as InstrumentServiceDetails | null
  );
  const [gateway, setGateway] = useState(null as InstrumentGateway | null);
  const [tags, setTags] = useState([] as Array<SchemaTag>);
  const [fieldInfos, setFieldInfos] = useState({} as {[id: string]: string | null});
  const [fieldValidationInfos, setFieldValidationInfos] = useState({} as {[id: string]: ValidationInfo});
  const [fieldTypes, setFieldTypes] = useState({} as {[id: string]: string | null});
  const [hasFetched, setHasFetched] = useState<boolean>(false);

  useEffect((): void => {
    getInstrumentServiceDetails(connectionId)
      .then((instrumentServiceDetails) => {
        setInstrumentServiceDetails(instrumentServiceDetails);
        getGateway(instrumentServiceDetails.gatewayId).then((gateway) => {
          setGateway(gateway);
          getAdapter(instrumentServiceDetails.instrumentName, gateway.gatewayType).then((adapter) => {
            setTags(
              adapter.fields
                .filter(
                  (field) =>
                    field.fieldType === ConnectionFieldType.TEXT ||
                    field.fieldType === ConnectionFieldType.SECRET_REF
                )
                .map((field) => convertToSchemaTag(field, instrumentServiceDetails))
            );

            setFieldInfos(Object.fromEntries(adapter.fields.map((field) => [field.id, field.info])));
            setFieldTypes(
              Object.fromEntries(
                adapter.fields.map((field) => [field.id, field.fieldType ? field.fieldType : null])
              )
            );
            setFieldValidationInfos(
              Object.keys(instrumentServiceDetails.fieldValidationInfoByName).length
                ? instrumentServiceDetails.fieldValidationInfoByName
                : Object.fromEntries(adapter.fields.map((field) => [field.id, {name: field.id}]))
            );
          });
        });
      })
      .finally(() => {
        setHasFetched(true);
      });
  }, [connectionId, hasFetched]);

  if (BenchlingConfig.INSTRUMENTS_ENABLE_CONNECTION_DETAILS_POLLING) {
    poll(() => {
      getInstrumentServiceDetails(connectionId).then((instrumentServiceDetails) => {
        setInstrumentServiceDetails(instrumentServiceDetails);
      });
    }, CONNECTION_POLL_INTERVAL_SEC * 1000);
  }

  if (result.type !== QueryResultType.SUCCESS) {
    return <LoadingFileScreen />;
  }

  const connection = result.data.connection;
  if (isInaccessible(connection)) {
    return <WorkspaceItemInaccessibleGuard inaccessibleInfo={connection} modelType="connection" />;
  }

  const status = hasFetched
    ? instrumentServiceDetails?.id != null
      ? instrumentServiceDetails.status
      : {
          value: ConnectionStatus.SERVICE_UNAVAILABLE,
          timestamp: new Date().toISOString(),
        }
    : null;
  return (
    <ConnectionDetails
      connection={connection}
      refetch={() => {
        setHasFetched(false);
        result.refetch({connectionId});
      }}
      gateway={gateway}
      status={status}
      tags={tags}
      fieldInfos={fieldInfos}
      fieldValidationInfos={fieldValidationInfos}
      fieldTypes={fieldTypes}
    />
  );
}
